import { useState } from "react";
import { useAuth } from "@clerk/react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  File01Icon,
  Edit02Icon,
  Settings02Icon,
  StarIcon,
  Delete01Icon,
} from "@hugeicons/core-free-icons";
import { MoreHorizontal, Star } from "lucide-react";
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
} from "@/components/ui/sidebar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { createProjectsApi } from "@/lib/api/projects";
import type { PaginatedProjectList } from "@/lib/api/projects";
import { RenameProjectDialog } from "@/components/project/RenameProjectDialog";
import { DeleteProjectDialog } from "@/components/project/DeleteProjectDialog";

type ActiveProject = { id: string; name: string };

export function NavRecents() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const projectsApi = createProjectsApi({ getToken });

  const [renameProject, setRenameProject] = useState<ActiveProject | null>(
    null,
  );
  const [deleteProject, setDeleteProject] = useState<ActiveProject | null>(
    null,
  );

  const recentsQueryKey = ["projects", "recents"];

  const { data: recentsData, isLoading } = useQuery({
    queryKey: recentsQueryKey,
    queryFn: () => projectsApi.list({ sortBy: "last_viewed_at", sortOrder: "desc", limit: 5 }),
  });

  const recentProjects = recentsData?.items ?? [];

  const starMutation = useMutation({
    mutationFn: (projectId: string) => projectsApi.star(projectId),
    onMutate: async (projectId) => {
      await queryClient.cancelQueries({ queryKey: recentsQueryKey });
      const prev = queryClient.getQueryData<PaginatedProjectList>(recentsQueryKey);
      queryClient.setQueryData<PaginatedProjectList>(recentsQueryKey, (old) => {
        if (!old) return old;
        return {
          ...old,
          items: old.items.map((p) =>
            p.id === projectId ? { ...p, isStarred: !p.isStarred } : p,
          ),
        };
      });
      return { prev };
    },
    onError: (_err, _id, ctx) => {
      queryClient.setQueryData(recentsQueryKey, ctx?.prev);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
  });

  return (
    <>
      <SidebarGroup className="group-data-[collapsible=icon]:hidden">
        <SidebarGroupLabel>Recents</SidebarGroupLabel>
        <SidebarMenu>
          {isLoading ? (
            <>
              <SidebarMenuSkeleton />
              <SidebarMenuSkeleton />
              <SidebarMenuSkeleton />
            </>
          ) : recentProjects.length === 0 ? (
            <SidebarMenuItem>
              <div className="px-2 py-1.5 text-xs text-muted-foreground">
                No recent projects
              </div>
            </SidebarMenuItem>
          ) : (
            recentProjects.map((project) => (
              <SidebarMenuItem key={project.id}>
                <SidebarMenuButton
                  asChild
                  tooltip={project.name}
                  className="group-hover/menu-item:bg-sidebar-accent group-hover/menu-item:text-sidebar-accent-foreground dark:group-hover/menu-item:bg-surface-raised-hover"
                >
                  <a href={`/projects/${project.id}`}>
                    <HugeiconsIcon icon={File01Icon} className="size-4" />
                    <span>{project.name}</span>
                  </a>
                </SidebarMenuButton>

                {/* Star badge — hides on hover and when the dropdown is open */}
                {project.isStarred && (
                  <SidebarMenuAction
                    aria-hidden
                    className="pointer-events-none transition-opacity group-hover/menu-item:opacity-0 group-has-data-[state=open]/menu-item:opacity-0"
                  >
                    <Star className="size-3 fill-amber-400 text-amber-400" />
                  </SidebarMenuAction>
                )}

                {/* More actions — fades in on hover, overlaps star position */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <SidebarMenuAction
                      showOnHover
                      className="hover:bg-black/10 dark:hover:bg-white/10 aria-expanded:bg-transparent"
                    >
                      <MoreHorizontal className="size-4" />
                    </SidebarMenuAction>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    side="right"
                    align="start"
                    className="w-48"
                    onCloseAutoFocus={(e) => e.preventDefault()}
                  >
                    <DropdownMenuItem
                      onClick={() => starMutation.mutate(project.id)}
                    >
                      <HugeiconsIcon
                        icon={StarIcon}
                        className={`size-4 ${project.isStarred ? "fill-amber-400 text-amber-400" : ""}`}
                      />
                      {project.isStarred ? "Unstar" : "Star"}
                    </DropdownMenuItem>

                    <DropdownMenuItem
                      onClick={() =>
                        setRenameProject({ id: project.id, name: project.name })
                      }
                    >
                      <HugeiconsIcon icon={Edit02Icon} className="size-4" />
                      Rename
                    </DropdownMenuItem>

                    <DropdownMenuItem asChild>
                      <a href={`/projects/${project.id}/settings`}>
                        <HugeiconsIcon
                          icon={Settings02Icon}
                          className="size-4"
                        />
                        Settings
                      </a>
                    </DropdownMenuItem>

                    <DropdownMenuSeparator />

                    <DropdownMenuItem
                      className="text-red-500 focus:text-red-500 focus:bg-red-500/10"
                      onClick={() =>
                        setDeleteProject({ id: project.id, name: project.name })
                      }
                    >
                      <HugeiconsIcon icon={Delete01Icon} className="size-4" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </SidebarMenuItem>
            ))
          )}
        </SidebarMenu>
      </SidebarGroup>

      {/* Dialogs — rendered outside the list to avoid stacking context issues */}
      {renameProject && (
        <RenameProjectDialog
          open={!!renameProject}
          onOpenChange={(open) => !open && setRenameProject(null)}
          projectId={renameProject.id}
          currentName={renameProject.name}
          getToken={getToken}
        />
      )}

      {deleteProject && (
        <DeleteProjectDialog
          open={!!deleteProject}
          onOpenChange={(open) => !open && setDeleteProject(null)}
          projectId={deleteProject.id}
          projectName={deleteProject.name}
          getToken={getToken}
        />
      )}
    </>
  );
}
